/**
 * Small pieces every Contract Intelligence page shares: the page frame, the
 * review status mark, severity counts, and the loading, empty and error
 * states — one look across Overview, Reviews, Documents and the review page.
 *
 * @module components/apps/contractIntelligence/ciParts
 */
import { Format, type HairlineProps } from "@lucasmarkes/hairline/react";
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  CircleDashedIcon,
  Loader2Icon,
  RotateCwIcon,
  SearchIcon,
} from "lucide-react";
import { useState, type ComponentType, type ReactNode } from "react";

import { cn } from "../../../lib/utils";
import type { CiReviewStatus, CiSeverity } from "../../../state/contractIntelligence";
import { Bone } from "../../regulation/LoadingSkeletons";
import { SeverityGlyph } from "../../regulation/finding/findingGlyphs";
import { Button } from "../../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from "../../ui/dialog";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../../ui/input-group";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "../../ui/empty";
import { HairlineArt } from "../../ui/hairline-art";
import { Tip } from "../../ui/tip";
import { SEVERITY_LABEL, SEVERITY_ORDER, STATUS_LABEL } from "./ciModel";

/** A page in the area: one header (title, a quiet meta, actions), then a scrolling body. */
export function CiPage({
  title,
  meta,
  actions,
  children,
  bodyClassName,
  ...rest
}: {
  readonly title: ReactNode;
  readonly meta?: ReactNode;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly bodyClassName?: string;
  readonly [data: `data-${string}`]: string | undefined;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-contract-intelligence="" {...rest}>
      <header className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border/60 px-4 py-2 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <h1 className="truncate text-[15px] font-semibold tracking-[-0.01em]">{title}</h1>
          {meta ? <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{meta}</span> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
      </header>
      <div className={cn("min-h-0 flex-1 overflow-y-auto", bodyClassName)}>{children}</div>
    </div>
  );
}

const STATUS_INK: Readonly<Record<CiReviewStatus, string>> = {
  draft: "text-muted-foreground",
  queued: "text-primary",
  running: "text-primary",
  completed: "text-emerald-600 dark:text-emerald-400",
  failed: "text-destructive",
};

/** The review's state as Linear draws an issue's: a 14px mark, optionally with its word. */
export function ReviewStatusMark({
  status,
  withLabel = false,
  className,
}: {
  readonly status: CiReviewStatus;
  readonly withLabel?: boolean;
  readonly className?: string | undefined;
}) {
  const icon =
    status === "completed" ? (
      <CheckCircle2Icon aria-hidden className="size-3.5" />
    ) : status === "failed" ? (
      <AlertTriangleIcon aria-hidden className="size-3.5" />
    ) : status === "draft" ? (
      <CircleDashedIcon aria-hidden className="size-3.5" />
    ) : (
      <Loader2Icon aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />
    );
  return (
    <span
      className={cn("inline-flex shrink-0 items-center gap-1.5", STATUS_INK[status], className)}
      role={withLabel ? undefined : "img"}
      aria-label={withLabel ? undefined : STATUS_LABEL[status]}
      title={withLabel ? undefined : STATUS_LABEL[status]}
      data-review-status={status}
    >
      {icon}
      {withLabel ? <span className="text-xs font-medium">{STATUS_LABEL[status]}</span> : null}
    </span>
  );
}

/** Open gaps by severity as compact glyph + count pairs; nothing when none are open. */
export function SeverityCounts({
  open,
  className,
}: {
  readonly open: Readonly<Record<CiSeverity, number>>;
  readonly className?: string;
}) {
  const shown = SEVERITY_ORDER.filter((severity) => open[severity] > 0);
  if (shown.length === 0) return null;
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      {shown.map((severity) => (
        <Tip key={severity} label={`${open[severity]} ${SEVERITY_LABEL[severity].toLowerCase()}`}>
          <span className="inline-flex items-center gap-0.5 text-xs tabular-nums text-muted-foreground">
            <SeverityGlyph severity={severity} className="size-3.5" />
            {open[severity]}
          </span>
        </Tip>
      ))}
    </span>
  );
}

/** Rows of bones shaped like the area's lists. */
export function CiListSkeleton({ rows = 6 }: { readonly rows?: number }) {
  return (
    <div className="divide-y divide-border/50" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex h-11 items-center gap-3 px-4 sm:px-6">
          <Bone className="size-3.5 rounded-full" />
          <Bone className="h-3 rounded" style={{ width: `${30 + ((index * 17) % 35)}%` }} />
          <span className="flex-1" />
          <Bone className="h-3 w-16 rounded" />
        </div>
      ))}
    </div>
  );
}

/** A read that failed: say so, and offer to try again. */
export function CiUnavailable({
  what,
  onRetry,
  className,
}: {
  readonly what: string;
  readonly onRetry: () => void;
  readonly className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-3 px-6 py-16 text-center", className)} role="alert">
      <AlertTriangleIcon aria-hidden className="size-5 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-sm font-medium">{what} could not be loaded</p>
        <p className="text-xs text-muted-foreground">The connection to the workspace dropped. Nothing was lost.</p>
      </div>
      <Button size="sm" variant="outline" onClick={onRetry} className="gap-1.5">
        <RotateCwIcon aria-hidden className="size-3.5" />
        Try again
      </Button>
    </div>
  );
}

export function CiEmpty({
  title,
  description,
  art = Format,
  children,
}: {
  readonly title: string;
  readonly description: ReactNode;
  readonly art?: ComponentType<HairlineProps>;
  readonly children?: ReactNode;
}) {
  return (
    <Empty className="py-16">
      <EmptyHeader className="max-w-md">
        <HairlineArt figure={art} className="mb-4 w-48 sm:w-56" />
        <EmptyTitle className="text-base text-foreground">{title}</EmptyTitle>
        <EmptyDescription className="text-sm text-muted-foreground">{description}</EmptyDescription>
      </EmptyHeader>
      {children ? <EmptyContent className="max-w-md">{children}</EmptyContent> : null}
    </Empty>
  );
}

/** A labelled band inside a page body: a small heading and its rows. */
export function CiSection({
  title,
  meta,
  action,
  children,
  className,
}: {
  readonly title: string;
  readonly meta?: ReactNode;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
}) {
  return (
    <section className={cn("space-y-2", className)} aria-label={title}>
      <div className="flex items-center gap-2 px-1">
        <h2 className="text-[13px] font-medium text-muted-foreground">{title}</h2>
        {meta ? <span className="text-xs tabular-nums text-muted-foreground/70">{meta}</span> : null}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

/** The area's search box: a magnifier and a quiet input. */
export function CiSearch({
  value,
  onChange,
  label,
  placeholder,
  className,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly label: string;
  readonly placeholder: string;
  readonly className?: string;
}) {
  return (
    <InputGroup className={cn("h-8 w-full rounded-lg sm:w-64", className)}>
      <InputGroupAddon>
        <SearchIcon aria-hidden className="size-3.5 text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupInput
        aria-label={label}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value) {
            event.preventDefault();
            onChange("");
          }
        }}
        className="text-[13px]"
      />
    </InputGroup>
  );
}

/**
 * Confirm a destructive step that affects everyone in the organization
 * (deleting a review, removing a template). The dialog stays open, and cannot
 * be dismissed, while the step runs; the caller closes it on success.
 */
export function CiConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  readonly title: string;
  readonly description: ReactNode;
  readonly confirmLabel: string;
  readonly onConfirm: () => Promise<void>;
  readonly onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(open) => (!open && !busy ? onClose() : undefined)}>
      <DialogPopup className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onConfirm().finally(() => setBusy(false));
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
